// `extension-alert-create-client-contact-modal.tpl.html` — "Contact Information", the form behind a
// contact profile's "Create in ATS", shown by `onCreateInATS` with
// `useUnifiedTemplate: true, windowClass: "dialog-unified shortlist-dialogue"`.
//
// ⚠️ ONE FORM, TWO WRITES. "Create new contact" and "Update existing contact" post the same
// endpoint (`createInAts`) with a different `context`, and the second button exists only where the
// tenant supports updating (`isUpdateClientContactInAtsSupported`). Both are ported; neither is sent
// from here — a press validates (`getValidationResponse`, read-only) and hands the body to
// `onSubmit`, which owns the write, its tracking and the waiting modal.
//
// ⚠️ EVERY TEXT FIELD TRIMS, AND THE BOX DOES NOT. AngularJS's `ng-trim` defaults to true, so the
// MODEL of " a@b.com " is "a@b.com" while the input keeps showing what was typed — and the model is
// what reaches the request body. `NgTextInput` reproduces exactly that split.
import { useEffect, useRef, useState, type InputHTMLAttributes, type ReactElement } from 'react'
import {
  AddAtsClientContactContext,
  getCreateClientContactExistingEmployers,
  getCreateClientContactValidationResponse,
  type AddAtsClientContactParams,
  type AddAtsClientContactValidationResponse,
  type EmployerAutoCompleteResult,
} from '@woo-ext/api'
import { UnifiedModal } from '../modal/UnifiedModal.tsx'
import { WooRadio } from '../common/WooRadio.tsx'
import { getAtsLabelArticle } from '../common/atsLabel.ts'
import { UibTooltipPopup, useHoverTooltip } from '../common/UibTooltip.tsx'
import {
  DISABLED_UPDATE_CLIENT_CONTACT_TOOLTIP,
  companiesAddedByWoo,
  createInAtsModalButtons,
  disabledCreateClientContactTooltip,
  formOnCompanyRadioChanged,
  formOnExistingEmployerSelected,
  formOnNewCompanySelected,
  initialCreateClientContactForm,
  submitCreateInAtsForm,
  type CreateClientContactForm,
  type CreateInAtsContact,
  type CreateInAtsState,
} from './createClientContact.ts'

export interface CreateClientContactModalProps {
  contact: CreateInAtsContact
  alertId?: number | undefined
  /** An identity: `createInAtsPreCheck`'s email rule keys on it. */
  atsName?: string | undefined
  /**
   * `ctrl.atsLabel` — the two company strings and the Create tooltip NAME the ATS by its label,
   * with its article: "Select an ATS company" (557c6a4dfa, 5cfd3ef899). Defaults to `atsName`.
   */
  atsLabel?: string | undefined
  state: CreateInAtsState
  /** `ctrl.createNewCompanyDisabled` — hides the radio pair, leaving "Select a company" only. */
  createNewCompanyDisabled?: boolean
  /** `ctrl.updateContactDetailsEnabled` — the "Get phone and email" link under the details. */
  updateContactDetailsEnabled?: boolean
  /** `ctrl.requestContactDetailsEnabled`; see `ClientContactSnippet`. */
  requestContactDetailsEnabled?: boolean
  requestContactDetailsTooltip?: string | undefined
  /** `ctrl.getContactDetailsCtaLabel()`. */
  contactDetailsCtaLabel: string
  /**
   * `ctrl.onGetContactDetailsClicked(true)`. ⚠️ NOT WIRED by any caller yet, for the snippet CTA's
   * reason: it spends the tenant's contact-lookup credits against a third-party provider.
   */
  onGetContactDetails?: () => void
  /** `typeaheads.getCreateClientContactExistingEmployers($viewValue)`. */
  searchEmployers?: (query: string) => Promise<readonly EmployerAutoCompleteResult[]>
  validate?: (params: AddAtsClientContactParams) => Promise<AddAtsClientContactValidationResponse>
  /** A press that passed both checks. The modal is closed by the caller (`scope.close()`). */
  onSubmit: (context: AddAtsClientContactContext, params: AddAtsClientContactParams) => void
  onClose: () => void
}

/**
 * `<input ng-model>` with AngularJS's text semantics: the model is the TRIMMED text, the box keeps
 * the raw text, `onChange` (`ng-change`) fires only when the model actually changes, and the box is
 * rewritten only when the model changes from OUTSIDE (a radio refilling the job title).
 */
function NgTextInput({
  value,
  onChange,
  ...rest
}: { value: string | null | undefined; onChange: (value: string) => void } & Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange'
>): ReactElement {
  const [text, setText] = useState(value ?? '')
  const seen = useRef(value)
  useEffect(() => {
    if (value === seen.current) return
    seen.current = value
    setText(value ?? '')
  }, [value])
  return (
    <input
      {...rest}
      value={text}
      onChange={(event) => {
        setText(event.target.value)
        const model = event.target.value.trim()
        if (model === seen.current) return
        seen.current = model
        onChange(model)
      }}
    />
  )
}

/**
 * The country list, verbatim — including its two disabled rules and the TODO upstream leaves on it
 * ("we should create one list which will be used both on client side and server side").
 */
const COUNTRIES: readonly string[] = [
  'Albania', 'Algeria', 'Angola', 'Argentina', 'Australia', 'Austria', 'Belarus', 'Belgium',
  'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Bulgaria', 'Canada', 'China', 'Colombia', 'Croatia',
  'Cyprus', 'Czech Republic', 'Denmark', 'Egypt', 'El Salvador', 'Estonia', 'Finland', 'France',
  'Georgia', 'Germany', 'Greece', 'Hong Kong', 'Hungary', 'Iceland', 'India', 'Ireland', 'Italy',
  'Japan', 'Kenya', 'Lithuania', 'Luxembourg', 'Malaysia', 'Mexico', 'Montenegro', 'Morocco',
  'Netherlands', 'New Zealand', 'Niger', 'Nigeria', 'Norway', 'Panama', 'Philippines', 'Poland',
  'Portugal', 'Qatar', 'Romania', 'Saudi Arabia', 'Serbia', 'Singapore', 'Slovakia', 'Slovenia',
  'South Africa', 'South Korea', 'Spain', 'Sweden', 'Switzerland', 'Taiwan', 'Tanzania', 'Thailand',
  'Tunisia', 'Turkey', 'Ukraine', 'United Arab Emirates', 'Zambia', 'Zimbabwe',
]
const PINNED_COUNTRIES: readonly string[] = ['United Kingdom', 'United States', 'Israel']
const RULE = '──────────'

/**
 * AngularJS's select with a model it has no option for renders an UNKNOWN option, selected and
 * blank (`? string:USA ?`), rather than showing the first option — so a company whose country is
 * not on the list opens with an empty box, not with "Unknown".
 */
function countrySelectValue(country: string | null | undefined): { value: string; unknown: boolean } {
  if (country === undefined || country === null || country === '') return { value: '', unknown: false }
  if (COUNTRIES.includes(country) || PINNED_COUNTRIES.includes(country)) return { value: country, unknown: false }
  return { value: `? string:${country} ?`, unknown: true }
}

/**
 * `uib-typeahead="employer for employer in …getCreateClientContactExistingEmployers($viewValue)"`
 * with `typeahead-editable="false"` and the one-line `…-employer-autocomplete.tpl.html` row.
 *
 * ⚠️ `typeahead-editable="false"` CLEARS THE MODEL, NOT THE PICK. Typing after a selection sets
 * `existingCompanyName` to `undefined` (blank: `null`) and leaves `existingCompanyExternalId`
 * exactly as the pick set it — so editing the text after choosing a company still submits that
 * company. That is upstream's behaviour and it is kept.
 */
function EmployerTypeahead({
  atsLabel,
  initial,
  search,
  onTyped,
  onSelect,
}: {
  atsLabel: string | undefined
  initial: string | null | undefined
  search: (query: string) => Promise<readonly EmployerAutoCompleteResult[]>
  onTyped: (model: null | undefined) => void
  onSelect: (item: EmployerAutoCompleteResult) => void
}): ReactElement {
  const [text, setText] = useState(initial ?? '')
  const [matches, setMatches] = useState<readonly EmployerAutoCompleteResult[]>([])
  const [active, setActive] = useState(0)
  const seq = useRef(0)
  const focused = useRef(false)
  const input = useRef<HTMLInputElement>(null)

  const query = (value: string): void => {
    const mine = (seq.current += 1)
    // `typeahead-min-length` defaults to 1, over the TRIMMED view value.
    if (value.length < 1) {
      setMatches([])
      return
    }
    void search(value).then(
      (results) => {
        // uib drops an answer that is not for the latest query, or that lands after blur.
        if (mine !== seq.current || !focused.current) return
        setMatches(results)
        // `typeahead-focus-first` defaults to true.
        setActive(0)
      },
      () => undefined,
    )
  }

  const choose = (item: EmployerAutoCompleteResult): void => {
    seq.current += 1
    setMatches([])
    setText(item.employerValue)
    onSelect(item)
  }

  const el = input.current
  return (
    <>
      <input
        ref={input}
        className="form-control input"
        type="text"
        placeholder={`Enter ${getAtsLabelArticle(atsLabel)} ${String(atsLabel)} company name`}
        autoComplete="off"
        value={text}
        onFocus={() => {
          focused.current = true
        }}
        onBlur={() => {
          focused.current = false
          // After a click on a match has landed, which the blur would otherwise pre-empt.
          setTimeout(() => {
            setMatches([])
          }, 150)
        }}
        onChange={(event) => {
          setText(event.target.value)
          const viewValue = event.target.value.trim()
          onTyped(viewValue === '' ? null : undefined)
          query(viewValue)
        }}
        onKeyDown={(event) => {
          if (matches.length === 0) return
          if (event.key === 'ArrowDown') setActive((i) => (i + 1) % matches.length)
          else if (event.key === 'ArrowUp') setActive((i) => (i - 1 + matches.length) % matches.length)
          else if (event.key === 'Enter' || event.key === 'Tab') {
            const item = matches[active]
            if (item === undefined) return
            event.preventDefault()
            choose(item)
            return
          } else if (event.key === 'Escape') {
            seq.current += 1
            setMatches([])
          } else return
          event.preventDefault()
        }}
      />
      {/* uib inserts the popup AFTER the input, positioned against it (`$position.position`). */}
      {matches.length === 0 ? null : (
        <ul
          className="dropdown-menu"
          role="listbox"
          style={{
            display: 'block',
            top: `${String((el?.offsetTop ?? 0) + (el?.offsetHeight ?? 0))}px`,
            left: `${String(el?.offsetLeft ?? 0)}px`,
          }}
        >
          {matches.map((item, index) => (
            <li
              key={`${item.employerExternalId}-${String(index)}`}
              // ⚠️ NO `uib-typeahead-match` CLASS: careero's ui-bootstrap popup template puts only
              // `active` on the row (measured against the dev tenant, 2 Nov).
              className={index === active ? 'active' : undefined}
              role="option"
              aria-selected={index === active}
              onMouseEnter={() => {
                setActive(index)
              }}
              onMouseDown={(event) => {
                event.preventDefault()
              }}
              onClick={() => {
                choose(item)
              }}
            >
              <a className="wl-employer-typeahead-item" tabIndex={-1}>
                <div>
                  <span>{item.employerValue}</span>
                </div>
              </a>
            </li>
          ))}
        </ul>
      )}
    </>
  )
}

/** A `uib-tooltip` with `tooltip-append-to-body` over one wrapper div. */
function useTooltipHost(content: string | undefined, enable: boolean) {
  const host = useRef<HTMLDivElement>(null)
  const hover = useHoverTooltip(content, enable)
  return { host, hover }
}

export function CreateClientContactModal({
  contact,
  alertId,
  atsName,
  atsLabel = atsName,
  state,
  createNewCompanyDisabled = false,
  updateContactDetailsEnabled = false,
  requestContactDetailsEnabled = true,
  requestContactDetailsTooltip,
  contactDetailsCtaLabel,
  onGetContactDetails,
  searchEmployers = getCreateClientContactExistingEmployers,
  validate = getCreateClientContactValidationResponse,
  onSubmit,
  onClose,
}: CreateClientContactModalProps): ReactElement {
  const [form, setForm] = useState<CreateClientContactForm>(() => initialCreateClientContactForm(contact))
  const [showMissingEmailError, setShowMissingEmailError] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [busy, setBusy] = useState<AddAtsClientContactContext | undefined>(undefined)

  const currentCompanies = companiesAddedByWoo(contact)
  const buttons = createInAtsModalButtons(state, form)
  const createCopy = disabledCreateClientContactTooltip(state, atsLabel)

  const detailsTip = useTooltipHost('The details below will be updated in the contact card and are available for editing.', true)
  const detailsIcon = useRef<HTMLElement>(null)
  const getDetailsTip = useTooltipHost(requestContactDetailsTooltip, !requestContactDetailsEnabled)
  const updateTip = useTooltipHost(DISABLED_UPDATE_CLIENT_CONTACT_TOOLTIP, buttons.updateTooltipEnabled)
  const createTip = useTooltipHost(createCopy, buttons.createTooltipEnabled)

  /** `onCreateClientContactClicked` / `onUpdateClientContactClicked` — see `submitCreateInAtsForm`. */
  const press = async (context: AddAtsClientContactContext): Promise<void> => {
    const outcome = await submitCreateInAtsForm({
      form,
      contact,
      alertId,
      atsName,
      context,
      validate,
      clearError: () => {
        setFormError(null)
      },
    })
    if (outcome.kind === 'error') {
      if (outcome.missingEmail) setShowMissingEmailError(true)
      setFormError(outcome.message)
      return
    }
    if (outcome.kind === 'submit') onSubmit(context, outcome.params)
  }

  /** `click-and-disable`: the button is `disabled` while its handler's promise is pending. */
  const clickAndDisable = (context: AddAtsClientContactContext): void => {
    if (busy !== undefined) return
    setBusy(context)
    void press(context).finally(() => {
      setBusy(undefined)
    })
  }

  const country = countrySelectValue(form.newCompanyCountry)

  return (
    <UnifiedModal open windowClass="dialog-unified shortlist-dialogue" onClose={onClose}>
      <div className="create-client-contact-modal-container">
        <div className="create-client-contact-modal-x-icon">
          <i className="far fa-times" onClick={onClose} />
        </div>
        <div>
          <div className="create-client-contact-title">Contact Information</div>
          <div className="mT4">
            {'Fill out the contact details for '}
            <b>{contact.name}</b>
          </div>
          <div className="create-client-contact-company-section-container">
            <div className="create-client-contact-section-title">Company information</div>
            {createNewCompanyDisabled ? null : (
              <div className="create-client-contact-company-section-radio-buttons">
                <div>
                  <WooRadio
                    name="createNewEmployer"
                    value="false"
                    checked={!form.createNewEmployer}
                    onChange={() => {
                      setForm((previous) => formOnCompanyRadioChanged(previous, false, contact))
                    }}
                  >
                    {`Select ${getAtsLabelArticle(atsLabel)} ${String(atsLabel)} company`}
                  </WooRadio>
                </div>
                <div>
                  <WooRadio
                    name="createNewEmployer"
                    value="true"
                    checked={form.createNewEmployer}
                    onChange={() => {
                      setForm((previous) => formOnCompanyRadioChanged(previous, true, contact))
                    }}
                  >
                    Create a new company
                  </WooRadio>
                </div>
              </div>
            )}
            {!form.createNewEmployer ? null : (
              <div className="select create-client-contact-company-section-new">
                <select
                  className="form-control input"
                  value={form.newCompanyId ? `number:${String(form.newCompanyId)}` : ''}
                  onChange={(event) => {
                    const id = Number.parseInt(event.target.value.replace(/^number:/, ''), 10)
                    setForm((previous) => formOnNewCompanySelected(previous, Number.isNaN(id) ? null : id, contact))
                  }}
                >
                  <option value="" disabled className="color-graylight">
                    Create a new company
                  </option>
                  {currentCompanies.map((company, index) => (
                    <option
                      key={`${String(company.companyId)}-${String(index)}`}
                      label={company.companyName}
                      value={`number:${String(company.companyId)}`}
                    >
                      {company.companyName}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {form.createNewEmployer ? null : (
              <div className="create-client-contact-company-section-existing">
                <EmployerTypeahead
                  atsLabel={atsLabel}
                  initial={form.existingCompanyName}
                  search={searchEmployers}
                  onTyped={(model) => {
                    setForm((previous) => ({ ...previous, existingCompanyName: model }))
                  }}
                  onSelect={(item) => {
                    setForm((previous) => formOnExistingEmployerSelected(previous, item))
                  }}
                />
              </div>
            )}
          </div>
          <div className="create-client-contact-job-title-container">
            <div className="create-client-contact-section-title">Job title</div>
            <div className="mT8">
              <NgTextInput
                className="form-control input"
                type="text"
                maxLength={50}
                value={form.jobTitle}
                onChange={(jobTitle) => {
                  setForm((previous) => ({ ...previous, jobTitle }))
                }}
              />
            </div>
          </div>
          {!form.createNewEmployer ? null : (
            <div className="create-client-contact-details-container">
              <div className="create-client-contact-section-title mB8">Company address</div>
              <div className="create-client-contact-details-row">
                <div className="create-client-contact-detail-container">
                  <label>Country</label>
                  <div className="select create-client-contact-detail">
                    <select
                      className="form-control input"
                      value={country.value}
                      onChange={(event) => {
                        const newCompanyCountry = event.target.value
                        setForm((previous) => ({ ...previous, newCompanyCountry }))
                      }}
                    >
                      {country.unknown ? <option value={country.value} /> : null}
                      <option value="">Unknown</option>
                      <option disabled>{RULE}</option>
                      {PINNED_COUNTRIES.map((name) => (
                        <option key={`pinned-${name}`} value={name}>
                          {name}
                        </option>
                      ))}
                      <option disabled>{RULE}</option>
                      {COUNTRIES.map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="create-client-contact-detail-container">
                  <label>Region/State</label>
                  <NgTextInput
                    className="form-control input create-client-contact-detail"
                    type="text"
                    value={form.newCompanyRegion}
                    onChange={(newCompanyRegion) => {
                      setForm((previous) => ({ ...previous, newCompanyRegion }))
                    }}
                  />
                </div>
              </div>
              <div className="create-client-contact-details-row">
                <div className="create-client-contact-detail-container">
                  <label>City</label>
                  <NgTextInput
                    className="form-control input create-client-contact-detail"
                    type="text"
                    value={form.newCompanyCity}
                    onChange={(newCompanyCity) => {
                      setForm((previous) => ({ ...previous, newCompanyCity }))
                    }}
                  />
                </div>
              </div>
            </div>
          )}
          {!form.createNewEmployer ? null : <div className="create-client-contact-divider" />}
          <div className="create-client-contact-details-and-update-container">
            <div className="create-client-contact-update-details clearfix">
              <div>
                <div className="create-client-contact-section-title">
                  Contact details{' '}
                  <i
                    ref={detailsIcon}
                    className="fas fa-info-circle create-client-contact-details-tooltip-icon"
                    onMouseEnter={detailsTip.hover.onMouseEnter}
                    onMouseLeave={detailsTip.hover.onMouseLeave}
                  />
                  {detailsTip.hover.open ? (
                    <UibTooltipPopup host={detailsIcon} placement="top">
                      The details below will be updated in the contact card and are available for editing.
                    </UibTooltipPopup>
                  ) : null}
                </div>
              </div>
              <div className="create-client-contact-details-row mT8">
                <div className="create-client-contact-detail-container">
                  <label>Email</label>
                  <NgTextInput
                    className={`form-control input create-client-contact-detail${
                      showMissingEmailError ? ' create-client-contact-missing-email' : ''
                    }`}
                    type="text"
                    name="clientContactEmail"
                    value={form.email}
                    onChange={(email) => {
                      setForm((previous) => ({ ...previous, email }))
                      // `onEmailChanged`.
                      setShowMissingEmailError(false)
                    }}
                  />
                </div>
                <div className="create-client-contact-detail-container">
                  <label>Phone</label>
                  <NgTextInput
                    className="form-control input create-client-contact-detail"
                    type="text"
                    value={form.phone}
                    onChange={(phone) => {
                      setForm((previous) => ({ ...previous, phone }))
                    }}
                  />
                </div>
              </div>
              {/* The progress and result branches need the get-details request, which is not
                  ported (see `onGetContactDetails`); before one, this is what renders. */}
              {!updateContactDetailsEnabled ? null : (
                <div className="create-client-contact-update-details-container pull-right mT8">
                  <div>
                    <div
                      ref={getDetailsTip.host}
                      className={`client-contact-get-latest-details-container${
                        requestContactDetailsEnabled ? '' : ' client-contact-snippet-link-disabled'
                      }`}
                      onMouseEnter={getDetailsTip.hover.onMouseEnter}
                      onMouseLeave={getDetailsTip.hover.onMouseLeave}
                    >
                      <a
                        href=""
                        onClick={(event) => {
                          // `<a href="">` is inert under AngularJS's `a` directive.
                          event.preventDefault()
                          onGetContactDetails?.()
                        }}
                      >
                        {contactDetailsCtaLabel}
                      </a>
                    </div>
                    {getDetailsTip.hover.open ? (
                      <UibTooltipPopup host={getDetailsTip.host} placement="top-left">
                        {requestContactDetailsTooltip}
                      </UibTooltipPopup>
                    ) : null}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="create-client-contact-footer">
            <div className="create-client-contact-result-container">
              {!formError ? null : <span className="create-client-contact-error-text">{formError}</span>}
            </div>
            <div className="create-client-contact-button-cancel-container">
              <div className="create-client-contact-button-cancel cursor-pointer" onClick={onClose}>
                Cancel
              </div>
              <div className="create-client-contact-update-create-buttons-container">
                {!state.updateSupported ? null : (
                  <div
                    ref={updateTip.host}
                    className={`update-client-contact-button-container${
                      buttons.updateDisabled ? ' client-contact-snippet-button-disabled' : ''
                    }`}
                    onMouseEnter={updateTip.hover.onMouseEnter}
                    onMouseLeave={updateTip.hover.onMouseLeave}
                  >
                    <button
                      type="button"
                      disabled={busy === AddAtsClientContactContext.UPDATE}
                      onClick={() => {
                        clickAndDisable(AddAtsClientContactContext.UPDATE)
                      }}
                    >
                      Update existing contact
                    </button>
                  </div>
                )}
                {updateTip.hover.open ? (
                  <UibTooltipPopup host={updateTip.host} placement="top-left">
                    {DISABLED_UPDATE_CLIENT_CONTACT_TOOLTIP}
                  </UibTooltipPopup>
                ) : null}
                <div
                  ref={createTip.host}
                  className={`create-client-contact-button-container${
                    buttons.createDisabled ? ' client-contact-snippet-button-disabled' : ''
                  }`}
                  onMouseEnter={createTip.hover.onMouseEnter}
                  onMouseLeave={createTip.hover.onMouseLeave}
                >
                  <button
                    type="button"
                    disabled={busy === AddAtsClientContactContext.CREATE}
                    onClick={() => {
                      clickAndDisable(AddAtsClientContactContext.CREATE)
                    }}
                  >
                    Create new contact
                  </button>
                </div>
                {createTip.hover.open ? (
                  <UibTooltipPopup host={createTip.host} placement="top-left">
                    {createCopy}
                  </UibTooltipPopup>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </UnifiedModal>
  )
}
